import parse from 'html-react-parser';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import { CartContext } from '@repo/contexts/CartContext';
import { CustomerContext } from '@repo/contexts/CustomerContext';
import { LocaleContext } from '@repo/contexts/LocaleContext';
import { FETCH_PRODUCT_UPDATE_QUERY } from '@repo/contexts/cartQueries';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { formatReviewSku, pushToDataLayer } from '@repo/utils';
import { TrackConversionAfterSearch } from '@repo/utils/algoliaAnalytics';
import canApplyProStaffDiscount from '@repo/utils/canApplyProStaffDiscount';
import { findMinMaxVariantPriceCad } from '@repo/utils/canadaStore';
import getPercentageDiscountPrice from '@repo/utils/getPercentageDiscountPrice';
import getVariantOptions from '@repo/utils/getVariantOptions';
import withTwoPassRendering from '@repo/utils/twoPassRendering';
import { BazaarVoiceContainer } from '../bazaarvoice';
import breakpoints from '../breakpoints';
import Button from '../button';
import IconArrow from '../images/chevron-down.svg';
import ProductMediaCarousel from '../product-media-carousel/ProductMediaCarousel';
import ProductOptionsSelector from '../product-options-selector';
import sharedStyles from '../styles';
import CustomerCareMessaging from './CustomerCareMessaging';
import DeliveryIndicator from './DeliveryIndicator';
import FreeInstallationIndicator from './FreeInstallationIndicator';
import FreeShippingIndicator from './FreeShippingIndicator';
import ProductDownloads from './ProductDownloads';
import ReturnPolicyIndicator from './ReturnPolicyIndicator';

const defaultTwoColumnBreakpoint = '768px';

const proStaffDiscount = process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE;
const showMetafields = process.env.GATSBY_METAFIELD_LIFECYCLE;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  @media (min-width: ${props =>
      props.twoColumnBreakpoint || defaultTwoColumnBreakpoint}) {
    flex-direction: row;
  }

  .image-gallery {
    background: ${({ theme }) => theme.colors.background};
  }

  .product-option-select,
  #quantity {
    background-color: white;
    background-image: url(${IconArrow});
    background-position: calc(100% - 13px) 15px;
    background-size: 1rem;
    background-repeat: no-repeat;
    height: 46px;
    border: 1px solid ${({ theme }) => theme.colors.inputBorder};
    border-radius: 0;
    color: ${({ theme }) => theme.colors.inputFontColor};
    padding: 0 2.5rem 0 1rem;
    margin: 0;
    appearance: none;
    width: 100%;
    @media screen and (min-width: ${breakpoints.lg}) {
      width: 50%;
    }
  }
  #quantity {
    width: auto;
  }
`;

const Details = styled.div`
  @media (min-width: ${props =>
      props.twoColumnBreakpoint || defaultTwoColumnBreakpoint}) {
    width: 60%;
    padding-top: 0;
    padding-left: 2rem;
  }
`;

const Description = styled.div`
  white-space: pre-line;
  margin-bottom: 1rem;
`;

const ProductMeta = styled.div`
  @media (min-width: ${props =>
      props.twoColumnBreakpoint || defaultTwoColumnBreakpoint}) {
    > div:first-child {
      margin-left: 0;
    }
  }
`;

const Price = styled.div`
  font-size: 1.75rem;
  margin-block: 1rem;
`;

const PreSalePrice = styled.span`
  text-decoration: line-through;
  font-size: 1.5rem;
`;

const SalePrice = styled.span`
  color: ${({ theme }) => theme.colors.onSaleRed};
`;

const CarouselArea = styled.div`
  @media (min-width: ${props =>
      props.twoColumnBreakpoint || defaultTwoColumnBreakpoint}) {
    width: 40%;
  }

  .image-gallery-image {
    height: auto;
    max-height: unset !important;
  }
`;

const QuantitySelector = styled.div`
  font-size: 1rem;
  margin-right: 1rem;

  label {
    span {
      margin-bottom: 0.25rem;
      display: block;
    }
  }

  label,
  select {
    display: block;
    font-size: 1rem;
  }
`;

const AttributeGrid = styled.ul`
  list-style: none;
  display: grid;
  gap: ${sharedStyles.spacing['spacing-sm']};
  grid-row-gap: 0;
  margin-bottom: ${sharedStyles.spacing['spacing-sm']};

  strong,
  span {
    display: block;
  }

  @media screen and (min-width: ${breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Attribute = styled.li`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${sharedStyles.spacing['spacing-sm']} 0;
  margin: 0;
`;

const AdditionalInformation = styled.div`
  padding: 1rem 0;

  .link {
    display: block;
    margin-bottom: 1rem;
  }
`;

const CheckoutActions = styled.div`
  display: flex;
  align-items: flex-end;
`;

const ProductInfoPanel = styled.div`
  padding-top: 1rem;
  min-height: 117px;
  @media screen and (min-width: ${breakpoints.md}) {
    min-height: 166px;
  }
`;

const ComingSoon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 6px;
`;

const Discontinued = styled.div`
  font-size: 1.5rem;
  margin-top: 1em;
`;

const fetchProductUpdate =
  (
    setIsAvailable,
    setSellingPlanGroups,
    shopifyClient,
    sanityProduct,
    currency
  ) =>
  async (shopifyId, currentVariant) => {
    if (!shopifyId || !currentVariant?.shopifyId) return;

    let shopifyProductId = shopifyId;

    if (currency === 'CAD') {
      const canadianVariantStore = sanityProduct?.store?.variants?.find(
        variant => variant.store.sku === currentVariant.sku
      )?.canadaStore;
      shopifyProductId = canadianVariantStore?.productGid;
    }

    if (shopifyProductId) {
      await shopifyClient
        .request(FETCH_PRODUCT_UPDATE_QUERY, {
          id: shopifyProductId,
        })
        .then(({ product }) => {
          if (product?.sellingPlanGroups) {
            setSellingPlanGroups(product?.sellingPlanGroups?.nodes || []);
          }

          product?.variants?.nodes?.forEach(variant => {
            if (currentVariant.sku === variant.sku)
              setIsAvailable(variant.availableForSale);
          });
        });
    } else {
      console.log(`Missing shopifyProductId`);
    }
  };

function ProductContainer({
  productData,
  product,
  sanityProduct,
  activeSku,
  children,
  downloadsSection,
  hideDescription,
  hasSizeChart,
  measureIconColor,
  loadOldBV,
  disableAddToCart,
  useEnhancedPDPMessaging,
  enhancedPDPMessages,
  atGlance,
  setActiveSku,
}) {
  const hasFreeInstallation = product?.tags?.includes('free-installation');
  const hasMoneyBackGuarantee = product?.tags?.includes('money-back-guarantee');

  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency || 'USD';
  const usLocale = currency === 'USD';

  const { shopifyClient, formatPrice } = useContext(CartContext);
  const context = useContext(CustomerContext);

  const [activeVariant, setActiveVariant] = useState(null);
  const [isAvailable, setIsAvailable] = useState(false);
  const [sellingPlanGroups, setSellingPlanGroups] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [showOptionErrorMessages, setShowOptionErrorMessages] = useState(false);
  const [isBrowser, setIsBrowser] = useState(false);
  const [variantLifecycle, setVariantLifecycle] = useState(null);

  const { tt } = useUiComponentsInternationalContext();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsBrowser(true);
    }
  }, []);

  const productLifecycle = product?.productLifecycle?.value || null;
  const AddToCartWithTwoPass = withTwoPassRendering(Button);
  const ProductOptionsSelectorWithTwoPass = withTwoPassRendering(
    ProductOptionsSelector
  );

  if (showMetafields === 'true' && product?.variants.length > 1) {
    const acceptedLifecyle = [
      undefined,
      'Available',
      'Coming Soon',
      'Discontinued',
      'Obsolete',
    ];
    // eslint-disable-next-line no-param-reassign
    product.variants = product?.variants.filter(v =>
      acceptedLifecyle.includes(v?.variantLifecycle)
    );
    // eslint-disable-next-line no-param-reassign
    product.options = getVariantOptions(product?.variants);
  }

  const checkAvailability = fetchProductUpdate(
    setIsAvailable,
    setSellingPlanGroups,
    shopifyClient,
    sanityProduct,
    currency
  );

  const handleQuantityChange = event => {
    setQuantity(parseInt(event.target.value, 10));
  };

  const optionChangeHandler = variantSku => {
    const selectedVariant = product?.variants.find(v => v.sku === variantSku);
    setActiveVariant(selectedVariant);
    setIsAvailable(selectedVariant?.availableForSale);
    setShowOptionErrorMessages(false); // reset optionErrorMessage flag
    let newUrl = `${window.location.protocol}//${window.location.host}/p/${product?.handle}`;
    if (selectedVariant?.sku) {
      newUrl = `${newUrl}/${selectedVariant.sku}`;
    }
    window.history.replaceState({ path: newUrl }, '', newUrl);
  };

  const [lookedAtSkus, setLookedAtSkus] = useState([]);

  useEffect(() => {
    const newActiveVariant =
      product?.variants.find(variant => variant.sku === activeSku) ||
      (product?.variants.length === 1 ? product?.variants[0] : null);
    setActiveVariant(newActiveVariant);
  }, [activeSku]);

  useEffect(() => {
    if (typeof window !== `undefined`) {
      setIsBrowser(true);
    }
    (async () => {
      await checkAvailability(product?.shopifyId, activeVariant);

      if (activeVariant && !lookedAtSkus.includes(activeVariant.sku)) {
        setLookedAtSkus([...lookedAtSkus, activeVariant.sku]);

        if (process.env.GATSBY_GA4_EVENTS) {
          const canadaVariant = sanityProduct?.store?.variants?.find(
            v => v?.store?.sku === activeVariant.sku
          )?.canadaStore;

          pushToDataLayer('view_item', {
            ecommerce: {
              currency,
              value:
                currency === 'CAD' ? canadaVariant.price : activeVariant.price,
            },
            items: [
              {
                variant: currency === 'CAD' ? canadaVariant : activeVariant,
                product,
              },
            ],
          });
        } else {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'gatsby-product-detail-view',
            ecommerce: {
              detail: {
                products: [
                  {
                    name: product?.title,
                    id: activeVariant.sku,
                    price: activeVariant.price,
                    brand: product?.vendor,
                    category: product?.productType,
                  },
                ],
              },
            },
          });
        }
      }
    })();
  }, [activeVariant, currency]);

  useEffect(() => {
    const lifecycle = activeVariant?.variantLifecycle;
    setVariantLifecycle(lifecycle || null);
    if (lifecycle === 'Coming Soon' || lifecycle === 'Obsolete')
      setIsAvailable(false);
  }, [activeVariant]);

  let priceV2amount = activeVariant?.price && parseFloat(activeVariant.price);

  // if no active variant, set priceV2amount to the products minVariantPrice
  if (!activeVariant) {
    priceV2amount = parseFloat(product?.priceRangeV2?.minVariantPrice.amount);
  }

  const compareAtPrice =
    activeVariant?.compareAtPrice && parseFloat(activeVariant.compareAtPrice);

  const activeCanadaVariant =
    activeVariant &&
    sanityProduct?.store?.variants?.find(
      variant => variant?.store?.sku === activeVariant?.sku
    )?.canadaStore;
  let priceV2amountCad =
    activeCanadaVariant?.price && parseFloat(activeCanadaVariant.price);
  const compareAtPriceCad =
    activeCanadaVariant?.compareAtPrice &&
    parseFloat(activeCanadaVariant.compareAtPrice);
  const minMaxVariantCadPriceRange =
    sanityProduct && findMinMaxVariantPriceCad(sanityProduct);

  if (!priceV2amountCad) {
    priceV2amountCad = parseFloat(
      minMaxVariantCadPriceRange?.minVariantPrice.amount
    );
  }

  const displayCadPriceRangeForProStaff = () => {
    const min = parseFloat(
      getPercentageDiscountPrice({
        price: minMaxVariantCadPriceRange?.minVariantPrice.amount,
        percentageDiscount: proStaffDiscount,
      })
    );
    const max = parseFloat(
      getPercentageDiscountPrice({
        price: minMaxVariantCadPriceRange?.maxVariantPrice.amount,
        percentageDiscount: proStaffDiscount,
      })
    );

    const range =
      min === max
        ? `${formatPrice(min)}`
        : `${formatPrice(min)} - ${formatPrice(max)}`;

    return <span>{range}</span>;
  };

  const displayCadPriceRange = () => {
    if (
      context?.isProStaff() &&
      canApplyProStaffDiscount(product?.tags, compareAtPriceCad)
    ) {
      return displayCadPriceRangeForProStaff();
    }

    const min = parseFloat(minMaxVariantCadPriceRange?.minVariantPrice.amount);
    const max = parseFloat(minMaxVariantCadPriceRange?.maxVariantPrice.amount);

    const range =
      min === max
        ? `${formatPrice(min)}`
        : `${formatPrice(min)} - ${formatPrice(max)}`;

    return <span>{range}</span>;
  };

  const getCadDisplayPrice = () => {
    // if there is no active variant, return the display price range
    if (!activeVariant) {
      return displayCadPriceRange();
    }

    // if there is a sale price, return the sale price component
    if (priceV2amountCad && compareAtPriceCad) {
      return (
        <>
          <PreSalePrice>{formatPrice(compareAtPriceCad)}</PreSalePrice>
          <br />
          <SalePrice>
            {tt(s => s.productCommon.salePrice, {
              amount: formatPrice(priceV2amountCad),
            })}
          </SalePrice>
        </>
      );
    }

    // if the user has a pro staff account and can apply the discount, return the discounted price component
    const isProStaff = context?.isProStaff?.();
    const proStaffDiscountPercentage =
      process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE;
    const canApplyDiscount =
      isProStaff && canApplyProStaffDiscount(product?.tags, compareAtPriceCad);
    if (priceV2amountCad && canApplyDiscount) {
      const discountedPrice = getPercentageDiscountPrice({
        price: priceV2amountCad,
        percentageDiscount: proStaffDiscountPercentage,
      });
      return (
        <>
          <strike>{formatPrice(priceV2amountCad)}</strike>{' '}
          <span className="updated-price">{formatPrice(discountedPrice)}</span>
        </>
      );
    }

    // if there is no compare at price or discount applied, return the regular price component
    return <span>{formatPrice(priceV2amountCad)}</span>;
  };

  const displayPriceRangeForProStaff = () => {
    const min = parseFloat(
      getPercentageDiscountPrice({
        price: product?.priceRangeV2.minVariantPrice.amount,
        percentageDiscount: proStaffDiscount,
      })
    );
    const max = parseFloat(
      getPercentageDiscountPrice({
        price: product?.priceRangeV2.maxVariantPrice.amount,
        percentageDiscount: proStaffDiscount,
      })
    );

    const range =
      min === max
        ? `${formatPrice(min)}`
        : `${formatPrice(min)} - ${formatPrice(max)}`;

    return <span>{range}</span>;
  };

  const displayPriceRange = () => {
    if (
      context?.isProStaff() &&
      canApplyProStaffDiscount(product?.tags, compareAtPrice)
    ) {
      return displayPriceRangeForProStaff();
    }

    const min = parseFloat(product?.priceRangeV2?.minVariantPrice.amount);
    const max = parseFloat(product?.priceRangeV2?.maxVariantPrice.amount);

    const range =
      min === max
        ? `${formatPrice(min)}`
        : `${formatPrice(min)} - ${formatPrice(max)}`;

    return <span>{range}</span>;
  };

  const getDisplayPrice = () => {
    // if there is no active variant, return the display price range
    if (!activeVariant) {
      return displayPriceRange();
    }

    // if there is a sale price, return the sale price component
    if (priceV2amount && compareAtPrice) {
      return (
        <>
          <PreSalePrice>{formatPrice(compareAtPrice)}</PreSalePrice>
          <br />
          <SalePrice>SALE {formatPrice(priceV2amount)}</SalePrice>
        </>
      );
    }

    // if the user has a pro staff account and can apply the discount, return the discounted price component
    const isProStaff = context?.isProStaff?.();
    const proStaffDiscountPercentage =
      process.env.GATSBY_PRO_STAFF_DISCOUNT_PERCENTAGE;
    const canApplyDiscount =
      isProStaff && canApplyProStaffDiscount(product?.tags, compareAtPrice);
    if (priceV2amount && canApplyDiscount) {
      const discountedPrice = getPercentageDiscountPrice({
        price: priceV2amount,
        percentageDiscount: proStaffDiscountPercentage,
      });
      return (
        <>
          <strike>{formatPrice(priceV2amount)}</strike>{' '}
          <span className="updated-price">{formatPrice(discountedPrice)}</span>
        </>
      );
    }

    // if there is no compare at price or discount applied, return the regular price component
    return <span>{formatPrice(priceV2amount)}</span>;
  };

  const getSlidePosition = () => {
    const index = product?.media?.findIndex(
      media => media.id === activeVariant?.media?.[0]?.id
    );
    return index > 0 ? index : 0;
  };

  const handleAvailability = () => {
    if (showMetafields === 'true') {
      if (
        productLifecycle === 'Coming Soon' ||
        variantLifecycle === 'Coming Soon'
      )
        return ' Coming Soon';
      if (productLifecycle === 'Obsolete' || variantLifecycle === 'Obsolete')
        return ' Discontinued';
    }
    return isAvailable
      ? ` ${tt(s => s.productCommon.inStock)}`
      : ` ${tt(s => s.productCommon.outOfStock)}`;
  };

  const handleQuantityDisable = () => {
    if (
      productLifecycle === 'Coming Soon' ||
      variantLifecycle === 'Coming Soon'
    )
      return true;
    return disableAddToCart;
  };

  return (
    <Wrapper>
      <CarouselArea className="carousel-area">
        <ProductMediaCarousel
          media={product?.media}
          selectedSlide={getSlidePosition()}
          showBullets={
            isBrowser &&
            window.innerWidth < parseInt(breakpoints.lg, 10) &&
            product?.media?.length > 1
          }
          showThumbnails={
            isBrowser && window.innerWidth > parseInt(breakpoints.lg, 10)
          }
          showNav
        />
      </CarouselArea>
      <Details className="product-container-details">
        <ProductInfoPanel>
          <h1>{product?.title}</h1>
          <ProductMeta>
            {activeVariant && (
              <div>
                {tt(s => s.productCommon.availabilityLabel)}
                {handleAvailability()}
              </div>
            )}
            {activeVariant && <div>SKU: {activeVariant?.sku}</div>}
          </ProductMeta>

          {productLifecycle !== 'Obsolete' &&
            variantLifecycle !== 'Obsolete' &&
            isBrowser && (
              <Price className="price">
                {(() => {
                  if (currency === 'CAD') {
                    if (activeVariant) {
                      return getCadDisplayPrice();
                    }
                    return displayCadPriceRange();
                  }
                  if (activeVariant) {
                    return getDisplayPrice();
                  }
                  return displayPriceRange();
                })()}
              </Price>
            )}

          {loadOldBV ? (
            <div
              data-bv-show="rating_summary"
              data-bv-product-id={formatReviewSku(
                activeVariant?.sku || product?.variants[0]?.sku
              )}
              data-bv-seo="false"
              className="mb-4"
            />
          ) : (
            <BazaarVoiceContainer id="BVRRSummaryContainer" />
          )}
        </ProductInfoPanel>

        {product?.options && productLifecycle !== 'Obsolete' && (
          <ProductOptionsSelectorWithTwoPass
            productData={product}
            activeVariant={activeVariant}
            hasSizeChart={hasSizeChart}
            optionChangeHandler={optionChangeHandler}
            measureIconColor={measureIconColor}
            showOptionErrorMessages={showOptionErrorMessages}
          />
        )}

        <CheckoutActions>
          {productLifecycle !== 'Obsolete' &&
            variantLifecycle !== 'Obsolete' && (
              <QuantitySelector>
                <label htmlFor="quantity">
                  {tt(s => s.productCommon.quantityLabel)}
                </label>
                <select
                  id="quantity"
                  value={quantity}
                  onChange={handleQuantityChange}
                  disabled={handleQuantityDisable()}
                >
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                  <option value={4}>4</option>
                  <option value={5}>5</option>
                </select>
              </QuantitySelector>
            )}
          <CartContext.Consumer>
            {({ addToCart }) => {
              if (
                productLifecycle === 'Coming Soon' ||
                variantLifecycle === 'Coming Soon'
              ) {
                return (
                  <ComingSoon>{tt(s => s.productCommon.comingSoon)}</ComingSoon>
                );
              }
              if (
                productLifecycle === 'Obsolete' ||
                variantLifecycle === 'Obsolete'
              ) {
                return (
                  <Discontinued>
                    {tt(s => s.productCommon.discontinued)}
                  </Discontinued>
                );
              }
              return (
                <AddToCartWithTwoPass
                  title={tt(s => s.productCommon.addToCartButton)}
                  ariaLabel={tt(s => s.productCommon.addToCartAriaLabel)}
                  disabled={disableAddToCart}
                  onClick={async () => {
                    if (
                      !activeVariant?.selectedOptions ||
                      activeVariant?.availableForSale === false ||
                      !isAvailable
                    )
                      setShowOptionErrorMessages(true);
                    else {
                      setShowOptionErrorMessages(false);
                      if (product?.searchedProduct)
                        TrackConversionAfterSearch(product?.searchedProduct, [
                          product?.shopifyId,
                        ]);

                      let shopifyVariantId = activeVariant.shopifyId;

                      if (currency === 'CAD') {
                        const canadianVariantStore =
                          sanityProduct?.store?.variants?.find(
                            variant => variant.store.sku === activeVariant.sku
                          )?.canadaStore;
                        shopifyVariantId = canadianVariantStore.gid;
                      }

                      await addToCart({
                        activeVariant,
                        product,
                        quantity,
                        sellingPlanGroups,
                        variantId: shopifyVariantId,
                        variantMetafields: activeVariant?.metafields,
                      });

                      pushToDataLayer('add_to_cart', {
                        ecommerce: {
                          currency,
                          value: (activeVariant.price * quantity).toFixed(2), // ! doesn't handle discounts/prostaff
                        },
                        items: [
                          {
                            variant: activeVariant,
                            product,
                            quantity,
                          },
                        ],
                      });
                    }
                  }}
                >
                  {tt(s => s.productCommon.addToCartButton)}
                </AddToCartWithTwoPass>
              );
            }}
          </CartContext.Consumer>
        </CheckoutActions>
        <AdditionalInformation>
          {atGlance?.length > 0 && (
            <AttributeGrid>
              {atGlance.map(productAttribute => (
                <Attribute
                  key={productAttribute.keyItem || productAttribute.valueItem}
                >
                  {productAttribute?.keyItem && (
                    <strong>{productAttribute.keyItem}</strong>
                  )}{' '}
                  {productAttribute?.valueItem && (
                    <span>{productAttribute.valueItem}</span>
                  )}
                </Attribute>
              ))}
            </AttributeGrid>
          )}
          {!hideDescription && product?.description && (
            <Description>{parse(product?.description)}</Description>
          )}

          {useEnhancedPDPMessaging && (
            <ul className="m-0 p-0">
              {/* hide the shipping indicator if isFreeInstallation = true  */}
              {/* if free-installation tag is added in shopify  */}
              {hasFreeInstallation && <FreeInstallationIndicator />}

              {/* if price is less than $50, display ‘delivery promise/timing’ 
          and ‘free shipping over $50’ messaging AND delivery timing is estimated at 3-5 days */}
              {!hasFreeInstallation && (
                <>
                  <DeliveryIndicator />
                  <FreeShippingIndicator
                    isSubscriptionProduct={
                      // loop through product.options and check for option called 'Frequency'
                      product?.options?.some(
                        option => option.name === 'Frequency'
                      )
                    }
                    isDigitalProduct={
                      // loop through product.variants and check if requiresShipping is false
                      product?.variants?.some(
                        variant => !variant.requiresShipping
                      )
                    }
                    productPrice={
                      currency === 'CAD'
                        ? compareAtPriceCad || priceV2amountCad
                        : compareAtPrice || priceV2amount
                    }
                    useEnhancedPDPMessaging={useEnhancedPDPMessaging}
                    enhancedPDPMessages={enhancedPDPMessages}
                  />
                </>
              )}

              <ReturnPolicyIndicator
                useEnhancedPDPMessaging={useEnhancedPDPMessaging}
                enhancedPDPMessages={enhancedPDPMessages}
                hasMoneyBackGuarantee={hasMoneyBackGuarantee}
              />

              <CustomerCareMessaging isUSLocale={usLocale} />
            </ul>
          )}

          {children}

          {(productData?.supportContent ||
            productData?.supportManuals?.length > 0 ||
            productData?.productManualLink) && (
            <ProductDownloads
              downloads={productData?.supportContent?.downloads || null}
              productManualLink={productData?.productManualLink}
              supportManuals={productData?.supportManuals}
              downloadsSection={downloadsSection}
            />
          )}
        </AdditionalInformation>
      </Details>
    </Wrapper>
  );
}

ProductContainer.propTypes = {
  product: PropTypes.shape({
    shopifyId: PropTypes.string,
    title: PropTypes.string,
    handle: PropTypes.string,
    description: PropTypes.string,
    availableForSale: PropTypes.bool,
    vendor: PropTypes.string,
    productType: PropTypes.string,
    productLifecycle: PropTypes.shape({
      value: PropTypes.string,
    }),
    priceRangeV2: PropTypes.shape({
      minVariantPrice: PropTypes.shape({
        amount: PropTypes.number.isRequired,
      }),
      maxVariantPrice: PropTypes.shape({
        amount: PropTypes.number.isRequired,
      }),
    }).isRequired,
    media: PropTypes.arrayOf(
      PropTypes.shape({
        image: PropTypes.shape({
          src: PropTypes.string,
        }),
      })
    ),
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        values: PropTypes.arrayOf(PropTypes.string),
      })
    ),
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        shopifyId: PropTypes.string,
        availableForSale: PropTypes.bool,
        sku: PropTypes.string,
        image: PropTypes.shape({ id: PropTypes.string }),
        price: PropTypes.number,
        compareAtPrice: PropTypes.number,
        selectedOptions: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.string,
          })
        ),
      })
    ),
    searchedProduct: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  sanityProduct: PropTypes.shape({
    store: PropTypes.shape({
      variants: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),
  activeSku: PropTypes.string,
  productData: PropTypes.shape({
    supportContent: PropTypes.shape({
      downloads: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    supportManuals: PropTypes.arrayOf(PropTypes.shape({})),
    productManualLink: PropTypes.string,
  }),
  children: PropTypes.node,
  downloadsSection: PropTypes.shape({
    background: PropTypes.string,
    textColor: PropTypes.string,
    heading: PropTypes.string,
    supportLink: PropTypes.string,
  }),
  hideDescription: PropTypes.bool,
  hasSizeChart: PropTypes.bool,
  measureIconColor: PropTypes.string,
  disableAddToCart: PropTypes.bool,
  loadOldBV: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  useEnhancedPDPMessaging: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  enhancedPDPMessages: PropTypes.shape({
    shippingPolicyLink: PropTypes.string,
    returnPolicyNode: PropTypes.node,
  }),
  atGlance: PropTypes.arrayOf(PropTypes.shape({})),
  setActiveSku: PropTypes.func,
};

ProductContainer.defaultProps = {
  productData: {},
  sanityProduct: {},
  activeSku: null,
  children: null,
  hideDescription: false,
  downloadsSection: null,
  hasSizeChart: false,
  measureIconColor: '#000',
  disableAddToCart: false,
  loadOldBV: false,
  atGlance: [],
  setActiveSku: undefined,
};

export default ProductContainer;
